// frontend/src/components/government/LevyWaiverTracker.js
import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DollarSign,
  Clock,
  CheckCircle,
  AlertTriangle,
  Send,
  Info,
  Shield,
  Plane,
  Stethoscope,
  FileText,
} from 'lucide-react';
import toast from '../../utils/toast';
import { staffAPI } from '../../services/api';
import LoadingSpinner from '../UI/LoadingSpinner';
import { StatusBadge } from '../UI/StatusBadge';
import { CountBadge } from '../UI/CountBadge';
import Modal from '../UI/Modal';
import FormSelect from '../UI/FormSelect';
import DebouncedSearchInput from '../UI/DebouncedSearchInput';
import { ShowHiddenToggleButton } from '../UI/ShowHiddenToggleButton';
import LevyWaiverSubmissionModal from './LevyWaiverSubmissionModal';
import { formatDate } from '../../utils/dateUtils';
import { formatLeaveDays, pluralizeDays } from '../../utils/numberUtils';

const formatSGD = (value) =>
  new Intl.NumberFormat('en-SG', { style: 'currency', currency: 'SGD' }).format(value || 0);

const TYPE_FILTER_OPTIONS = [
  { value: 'all', label: 'All Waiver Types' },
  { value: 'overseas_leave', label: 'Overseas Leave' },
  { value: 'hospitalisation', label: 'Hospitalisation Leave' },
];

// Maps a tracker claim_status to a StatusBadge status + readable label
const STATUS_META = {
  to_claim: { badge: 'pending', label: 'To Claim' },
  pending: { badge: 'pending', label: 'Draft' },
  submitted: { badge: 'submitted', label: 'Submitted to MOM' },
  approved: { badge: 'completed', label: 'Waived' },
  rejected: { badge: 'rejected', label: 'Rejected' },
  cancelled: { badge: 'pending', label: 'Cancelled' },
  not_eligible: { badge: 'pending', label: 'Not Eligible' },
};

const STATUS_BUCKETS = [
  { value: 'all', label: 'All' },
  { value: 'to_claim', label: 'To Claim' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'waived', label: 'Waived' },
  { value: 'overdue', label: 'Overdue' },
];

const inBucket = (entry, bucket) => {
  switch (bucket) {
    case 'to_claim':
      return entry.claim_status === 'to_claim';
    case 'in_progress':
      return ['pending', 'submitted'].includes(entry.claim_status);
    case 'waived':
      return entry.claim_status === 'approved';
    case 'overdue':
      return entry.is_overdue;
    default:
      return true;
  }
};

const WaiverTypeBadge = ({ waiverType }) => {
  const isOverseas = waiverType === 'overseas_leave';
  const Icon = isOverseas ? Plane : Stethoscope;
  const tone = isOverseas ? 'bg-sky-50 text-sky-700' : 'bg-violet-50 text-violet-700';
  const label = isOverseas ? 'Overseas Leave' : 'Hospitalisation';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${tone}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
};

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = [currentYear, currentYear - 1, currentYear - 2].map((y) => ({
  value: String(y),
  label: String(y),
}));

const LevyWaiverTracker = () => {
  const queryClient = useQueryClient();
  const [year, setYear] = useState(String(currentYear));
  const [statusBucket, setStatusBucket] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showIneligible, setShowIneligible] = useState(false);

  // Record-waiver modal state
  const [recordEntry, setRecordEntry] = useState(null);
  const [recordStatus, setRecordStatus] = useState('submitted');
  const [recordRef, setRecordRef] = useState('');
  const [recordAmount, setRecordAmount] = useState('');
  const [overseasDestination, setOverseasDestination] = useState('');
  const [overseasReturnDate, setOverseasReturnDate] = useState('');
  const [medicalInstitution, setMedicalInstitution] = useState('');
  const [doctorName, setDoctorName] = useState('');
  const [mcNumber, setMcNumber] = useState('');

  // Submission-detail modal state
  const [detailId, setDetailId] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['levy-waiver-tracker', { year, showIneligible }],
    queryFn: async () => {
      const res = await staffAPI.getLevyWaiverTracker({
        year,
        ...(showIneligible ? { include_ineligible: 'true' } : {}),
      });
      return res.data;
    },
    staleTime: 60 * 1000,
  });

  const recordMutation = useMutation({
    mutationFn: (payload) => staffAPI.recordLevyWaiver(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['levy-waiver-tracker'] });
      queryClient.invalidateQueries({ queryKey: ['levy-waivers'] });
      toast.success('Levy waiver status updated');
      closeRecord();
    },
    onError: (error) => {
      const msg = error?.response?.data?.error || 'Failed to update levy waiver status';
      toast.error(String(msg));
    },
  });

  const summary = data?.summary || {};
  const entries = useMemo(() => data?.entries || [], [data]);

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return entries.filter((e) => {
      if (!inBucket(e, statusBucket)) return false;
      if (typeFilter !== 'all' && e.waiver_type !== typeFilter) return false;
      if (term) {
        const hay = `${e.employee_name || ''} ${e.staff_id || ''} ${e.leave_type_name || ''}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [entries, statusBucket, typeFilter, searchTerm]);

  const openRecord = (entry, presetStatus) => {
    setRecordEntry(entry);
    setRecordStatus(presetStatus);
    setRecordRef(entry.mom_reference_number || '');
    setRecordAmount(entry.amount ? String(entry.amount) : '');
    // Destination is not auto-filled: the flight ticket stores a single arrival
    // airport, which on round-trips is the return leg (Singapore). HR enters the
    // verified destination themselves (optional, internal record only — MOM does
    // not require it for the overseas-leave waiver).
    setOverseasDestination('');
    setOverseasReturnDate(
      entry.flight_return_date || entry.waiver_end_date || entry.leave_end_date || ''
    );
    setMedicalInstitution('');
    setDoctorName('');
    setMcNumber('');
  };

  const closeRecord = () => {
    setRecordEntry(null);
    setRecordRef('');
    setRecordAmount('');
    setOverseasReturnDate('');
  };

  const submitRecord = (e) => {
    e.preventDefault();
    const payload = {
      leave_application_id: recordEntry.leave_application_id,
      waiver_status: recordStatus,
      mom_reference_number: recordRef || undefined,
      levy_amount_waived: recordAmount ? parseFloat(recordAmount) : undefined,
    };
    if (recordEntry.waiver_type === 'overseas_leave') {
      payload.overseas_destination = overseasDestination || undefined;
      payload.overseas_return_date = overseasReturnDate || undefined;
    } else if (recordEntry.waiver_type === 'hospitalisation') {
      payload.medical_institution = medicalInstitution || undefined;
      payload.doctor_name = doctorName || undefined;
      payload.mc_number = mcNumber || undefined;
    }
    recordMutation.mutate(payload);
  };

  // The period shown is the physical-absence window the waiver is valued over:
  // for overseas leave that is the flight-ticket window (departure -> return),
  // otherwise the leave/MC dates.
  const periodText = (entry) => {
    const startRaw = entry.waiver_start_date || entry.leave_start_date;
    if (!startRaw) return '—';
    const endRaw = entry.waiver_end_date || entry.leave_end_date || startRaw;
    const start = formatDate(startRaw);
    const end = formatDate(endRaw);
    if (start === end) return start;
    return (
      <span className="whitespace-nowrap">
        <span className="tabular-nums">{start}</span>
        <span className="text-gray-400 mx-0.5" aria-hidden="true">→</span>
        <span className="tabular-nums">{end}</span>
      </span>
    );
  };

  // Small flight-ticket provenance line for overseas leave rows.
  const flightMeta = (entry) => {
    if (entry.waiver_type !== 'overseas_leave') return null;
    if (entry.period_source === 'employee_declaration') {
      return (
        <div
          className="mt-1 inline-flex items-center gap-1 text-[11px] text-sky-600"
          title="Overseas window declared by the employee at leave application. Confirm the actual return date before recording the MOM claim."
        >
          <Plane className="w-3 h-3" />
          {entry.return_date_pending
            ? 'Employee-declared dates · return pending'
            : 'Employee-declared dates'}
        </div>
      );
    }
    if (entry.return_date_pending) {
      return (
        <div
          className="mt-1 inline-flex items-center gap-1 text-[11px] text-amber-600"
          title="One-way ticket (Urgent Leave): departure verified from the flight ticket; the return date is confirmed on re-entry. Provisionally valued to the leave end date — confirm the actual return when recording."
        >
          <Plane className="w-3 h-3" />
          {entry.has_flight_ticket ? 'Departure per ticket · return pending' : 'Return date pending'}
        </div>
      );
    }
    if (entry.has_flight_ticket) {
      return (
        <div
          className="mt-1 inline-flex items-center gap-1 text-[11px] text-sky-600"
          title="Overseas window verified from the round-trip flight ticket (Singapore departure and return)."
        >
          <Plane className="w-3 h-3" />
          Per flight ticket
        </div>
      );
    }
    return (
      <div
        className="mt-1 inline-flex items-center gap-1 text-[11px] text-gray-400"
        title="No flight ticket captured yet — valued from the leave dates. Confirm the ticket before claiming."
      >
        <Plane className="w-3 h-3" />
        No ticket on file
      </div>
    );
  };

  const eligibilityText = (entry) => {
    if (!entry.is_eligible) {
      return (
        <span className="inline-flex items-center gap-1 text-amber-600 text-xs">
          <AlertTriangle className="w-3.5 h-3.5" />
          {entry.waiver_type === 'overseas_leave' ? '< 7 days' : 'Not eligible'}
        </span>
      );
    }
    return (
      <span>
        {formatLeaveDays(entry.eligible_days)} {pluralizeDays(entry.eligible_days)}
        {entry.annual_cap_capped && (
          <span className="block text-[11px] text-amber-600">capped at 60/yr</span>
        )}
      </span>
    );
  };

  const renderDeadline = (entry) => {
    if (!entry.application_deadline) return <span className="text-gray-400">—</span>;
    if (entry.claim_status === 'approved') {
      return <span className="text-gray-500">{formatDate(entry.application_deadline)}</span>;
    }
    if (entry.is_overdue) {
      return (
        <span className="inline-flex items-center text-red-600 font-medium">
          <AlertTriangle className="w-3.5 h-3.5 mr-1" />
          Overdue
        </span>
      );
    }
    const days = entry.days_until_deadline;
    const soon = typeof days === 'number' && days <= 60;
    return (
      <span className={soon ? 'text-amber-600 font-medium' : 'text-gray-700'}>
        {formatDate(entry.application_deadline)}
        {typeof days === 'number' && (
          <span className="block text-xs text-gray-400">{days} day(s) left</span>
        )}
        {!entry.submission_window_open && entry.window_note && (
          <span className="block text-[11px] text-amber-600">{entry.window_note}</span>
        )}
      </span>
    );
  };

  const renderActions = (entry) => {
    const stop = (fn) => (e) => {
      e.stopPropagation();
      fn();
    };
    if (!entry.is_worklist || !entry.is_eligible) {
      return <span className="text-xs text-gray-400">No claim</span>;
    }
    const isUnclaimed = ['to_claim', 'pending', 'rejected'].includes(entry.claim_status);
    const isSubmitted = entry.claim_status === 'submitted';
    // MOM window: can only submit from the 1st of the month after the levy bill,
    // and (overseas) only after the worker has returned to Singapore.
    const canSubmit = entry.submission_window_open;
    return (
      <div className="flex items-center justify-end gap-1">
        <button
          onClick={stop(() => setDetailId(entry.leave_application_id))}
          className="icon-button-admin"
          title="View levy-waiver submission details"
          aria-label={`View levy-waiver submission details for ${entry.employee_name || entry.staff_id}`}
        >
          <FileText className="w-4 h-4" />
        </button>
        {isUnclaimed && !canSubmit && entry.window_note && (
          <span
            className="inline-flex items-center gap-1 text-[11px] text-amber-600"
            title={entry.window_note}
          >
            <Clock className="w-3.5 h-3.5" />
            {entry.worker_returned ? 'Not open yet' : 'Awaiting return'}
          </span>
        )}
        {isUnclaimed && canSubmit && (
          <button
            onClick={stop(() => openRecord(entry, 'submitted'))}
            className="icon-button-admin"
            title="Mark submitted to MOM"
            aria-label={`Mark levy waiver submitted for ${entry.employee_name || entry.staff_id}`}
          >
            <Send className="w-4 h-4" />
          </button>
        )}
        {isSubmitted && (
          <button
            onClick={stop(() => openRecord(entry, 'approved'))}
            className="icon-button-admin"
            title="Mark waived by MOM"
            aria-label={`Mark levy waiver approved for ${entry.employee_name || entry.staff_id}`}
          >
            <CheckCircle className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
                    {formatSGD(summary.to_claim_amount)}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600 truncate">
                    To Claim ({summary.to_claim_count || 0})
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Send className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
                    {formatSGD(summary.in_progress_amount)}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600 truncate">
                    In Progress ({summary.in_progress_count || 0})
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
                    {formatSGD(summary.waived_amount)}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600 truncate">
                    Levy Waived ({summary.waived_count || 0})
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
              <div className="flex items-start">
                <div className="w-9 h-9 sm:w-12 sm:h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-4 h-4 sm:w-6 sm:h-6 text-red-600" />
                </div>
                <div className="ml-2 sm:ml-4 min-w-0">
                  <p className="text-lg sm:text-2xl font-bold text-gray-900">{summary.overdue_count || 0}</p>
                  <p className="text-xs sm:text-sm text-gray-600 truncate">Overdue (past 1-yr)</p>
                </div>
              </div>
            </div>
          </div>

          {/* Toolbar: status buckets + filters */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth">
              {STATUS_BUCKETS.map((b) => (
                <button
                  key={b.value}
                  onClick={() => setStatusBucket(b.value)}
                  className={`tab-btn-admin tab-btn-admin-sm ${statusBucket === b.value ? 'active' : ''}`}
                >
                  {b.label}
                  {b.value === 'to_claim' && summary.to_claim_count > 0 && (
                    <CountBadge count={summary.to_claim_count} theme="admin" active={statusBucket === 'to_claim'} />
                  )}
                  {b.value === 'overdue' && summary.overdue_count > 0 && (
                    <CountBadge count={summary.overdue_count} theme="warning" active={statusBucket === 'overdue'} />
                  )}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <DebouncedSearchInput
                value={searchTerm}
                onSearch={setSearchTerm}
                placeholder="Search by employee or staff ID..."
                theme="admin"
                className="min-w-[200px] max-w-xl"
              />
              <div className="w-52 flex-shrink-0">
                <FormSelect
                  options={TYPE_FILTER_OPTIONS}
                  value={typeFilter}
                  onChange={(v) => setTypeFilter(typeof v === 'object' ? v?.target?.value : v)}
                />
              </div>
              <div className="w-28 flex-shrink-0">
                <FormSelect
                  options={YEAR_OPTIONS}
                  value={year}
                  onChange={(v) => setYear(typeof v === 'object' ? v?.target?.value : v)}
                />
              </div>
              <ShowHiddenToggleButton
                isActive={showIneligible}
                hiddenCount={summary.hidden_ineligible_count || 0}
                onClick={() => setShowIneligible((v) => !v)}
                titleOff="Show ineligible leaves (overseas under 7 days)"
                titleOn="Hide ineligible leaves"
              />
            </div>
            {!showIneligible && summary.hidden_ineligible_count > 0 && (
              <p className="text-xs text-gray-500">
                {summary.hidden_ineligible_count} ineligible leave
                {summary.hidden_ineligible_count === 1 ? '' : 's'} hidden — overseas leave under 7
                consecutive days is not levy-waivable. Enable “Show ineligible” to audit them.
              </p>
            )}
          </div>

          {/* Tracker list */}
          {filtered.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-sm border">
              <div className="text-center py-12">
                <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No levy waivers to track</h3>
                <p className="text-sm text-gray-500 max-w-xl mx-auto">
                  No levy-waivable leave for {year}. WP / S Pass holders appear here after an
                  approved overseas absence of 7+ consecutive calendar days (worker Home / Urgent
                  Leave with a flight ticket, or levy-liable staff who declared they left
                  Singapore) or a hospitalisation leave, once a matching MOM levy bill is uploaded.
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block bg-white rounded-2xl shadow-sm border overflow-hidden">
                <table className="table">
                  <thead className="table-header">
                    <tr>
                      <th className="table-header-cell">Employee</th>
                      <th className="table-header-cell">Waiver Type</th>
                      <th className="table-header-cell">Absence Period</th>
                      <th className="table-header-cell">Waivable Days</th>
                      <th className="table-header-cell">Est. Levy Waived</th>
                      <th className="table-header-cell">Apply By</th>
                      <th className="table-header-cell">Status</th>
                      <th className="table-header-cell text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="table-body">
                    {filtered.map((entry) => (
                      <tr
                        key={entry.leave_application_id}
                        onClick={entry.is_worklist ? () => setDetailId(entry.leave_application_id) : undefined}
                        className={`table-row${!entry.is_eligible ? ' opacity-60' : ''}${entry.is_worklist ? ' cursor-pointer' : ''}`}
                      >
                        <td className="table-cell">
                          <div className="font-medium text-gray-900">{entry.employee_name}</div>
                          <div className="text-xs text-gray-500">
                            {entry.staff_id}
                            {entry.pass_type ? ` · ${entry.pass_type}` : ''}
                          </div>
                        </td>
                        <td className="table-cell">
                          <WaiverTypeBadge waiverType={entry.waiver_type} />
                          <div className="text-[11px] text-gray-400 mt-0.5">{entry.leave_type_name}</div>
                        </td>
                        <td className="table-cell text-sm text-gray-700">
                          <div>{periodText(entry)}</div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {formatLeaveDays(entry.total_days)} {pluralizeDays(entry.total_days)}
                          </div>
                          {flightMeta(entry)}
                        </td>
                        <td className="table-cell text-sm text-gray-700">
                          <div>{eligibilityText(entry)}</div>
                          {entry.daily_rate > 0 && (
                            <div className="text-xs text-gray-400">@ {formatSGD(entry.daily_rate)}/day</div>
                          )}
                        </td>
                        <td className="table-cell font-semibold tabular-nums">
                          {formatSGD(entry.amount)}
                          {(entry.amount_is_estimated || !entry.levy_bill_present) && (
                            <span
                              className="block text-[11px] text-amber-600"
                              title="No MOM levy bill uploaded for this leave month — estimated from the latest available bill."
                            >
                              estimated
                            </span>
                          )}
                          {entry.levy_bill_month && entry.levy_bill_present && (
                            <span className="block text-[11px] text-gray-400">bill {entry.levy_bill_month}</span>
                          )}
                        </td>
                        <td className="table-cell text-sm">{renderDeadline(entry)}</td>
                        <td className="table-cell">
                          <StatusBadge
                            status={STATUS_META[entry.claim_status]?.badge || 'pending'}
                            label={STATUS_META[entry.claim_status]?.label}
                          />
                        </td>
                        <td className="table-cell">{renderActions(entry)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden space-y-3">
                {filtered.map((entry) => (
                  <div
                    key={entry.leave_application_id}
                    onClick={entry.is_worklist ? () => setDetailId(entry.leave_application_id) : undefined}
                    className={`bg-white rounded-2xl shadow-sm border p-4 hover:shadow-md transition-shadow flex flex-col h-full${!entry.is_eligible ? ' opacity-60' : ''}${entry.is_worklist ? ' cursor-pointer' : ''}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-gray-900 truncate">{entry.employee_name}</div>
                        <div className="text-xs text-gray-500">
                          {entry.staff_id}
                          {entry.pass_type ? ` · ${entry.pass_type}` : ''}
                        </div>
                        <div className="mt-1">
                          <WaiverTypeBadge waiverType={entry.waiver_type} />
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        <StatusBadge
                          status={STATUS_META[entry.claim_status]?.badge || 'pending'}
                          label={STATUS_META[entry.claim_status]?.label}
                        />
                      </div>
                    </div>
                    <div className="text-sm text-gray-700 flex-1">{periodText(entry)}</div>
                    {flightMeta(entry)}
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-xs text-gray-500">{eligibilityText(entry)} waivable</span>
                      <span className="font-semibold tabular-nums">{formatSGD(entry.amount)}</span>
                    </div>
                    <div className="mt-1 text-xs">Apply by: {renderDeadline(entry)}</div>
                    <div className="card-action-footer justify-end gap-2">
                      {renderActions(entry)}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Reference panel */}
          <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5">
            <h4 className="font-medium text-gray-900 mb-2 flex items-center">
              <Info className="w-4 h-4 mr-2 text-blue-600" />
              How levy waivers are tracked (MOM)
            </h4>
            <ul className="text-sm text-blue-900 space-y-1 list-disc list-inside">
              <li>Only Work Permit / S Pass holders incur a levy, so only people on an uploaded MOM levy bill appear here — including levy-liable staff (for example S Pass).</li>
              <li><strong>Overseas leave:</strong> waivable when they are physically outside Singapore for ≥ <strong>7 consecutive calendar days</strong>; capped at <strong>60 calendar days/year</strong>. MOM verifies re-entry — this is not deducted leave days. The absence window comes from the worker’s <strong>flight ticket</strong> (Home Leave round-trip, Urgent Leave one-way with return confirmed on re-entry) or from a <strong>staff declaration</strong> on Annual / Unpaid Leave. Confirm the actual return before claiming. A “staying in Singapore” answer never appears here.</li>
              <li><strong>Hospitalisation leave:</strong> waivable when issued by a Singapore-registered doctor at a local medical institution; capped at <strong>60 calendar days/year</strong>. Keep the MC (worker name/FIN, period, clinic/hospital, MC number, doctor’s details).</li>
              <li>Apply from the 1st of the month after the levy bill, and <strong>within 1 year</strong> of the levy bill month. Apply after they return / are discharged.</li>
              <li>Amounts are estimated from the billed daily levy rate; MOM confirms the exact waiver. Submit on <a className="underline" href="https://www.mom.gov.sg/eservices/services/check-and-pay-levy" target="_blank" rel="noreferrer">myMOM / WP Online</a>.</li>
            </ul>
            <p className="text-xs text-blue-700 mt-2">
              Staff who answered No (staying in Singapore) are not levy-waiver candidates.
              Source: mom.gov.sg — Apply for levy waiver.
            </p>
          </div>
        </>
      )}

      {/* Record waiver modal */}
      <Modal
        isOpen={!!recordEntry}
        onClose={closeRecord}
        title={recordStatus === 'approved' ? 'Mark Levy Waiver Approved' : 'Mark Levy Waiver Submitted'}
        size="md"
        theme="admin"
        footer={
          <>
            <button type="button" onClick={closeRecord} className="btn btn-admin-outline">
              Cancel
            </button>
            <button
              type="submit"
              form="record-waiver-form"
              disabled={recordMutation.isPending}
              className="btn btn-success"
            >
              {recordMutation.isPending ? 'Saving...' : 'Save'}
            </button>
          </>
        }
      >
        {recordEntry && (
          <form id="record-waiver-form" onSubmit={submitRecord} className="space-y-4">
            <div className="rounded-2xl bg-gray-50 p-4 text-sm">
              <div className="flex items-center gap-2 mb-1">
                <Shield className="w-4 h-4 text-red-600" />
                <span className="font-medium text-gray-900">{recordEntry.employee_name}</span>
                <span className="text-gray-500">({recordEntry.staff_id})</span>
              </div>
              <div className="text-gray-600 flex items-center gap-2 flex-wrap">
                <WaiverTypeBadge waiverType={recordEntry.waiver_type} />
                <span className="text-gray-400">·</span>
                <span>{periodText(recordEntry)}</span>
              </div>
              <div className="text-gray-600 mt-1">{recordEntry.basis}</div>
            </div>

            <div className="form-group">
              <label className="form-label">MOM Reference Number</label>
              <input
                type="text"
                placeholder="e.g., LW-2026-001234"
                value={recordRef}
                onChange={(e) => setRecordRef(e.target.value)}
                className="input-admin w-full"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Levy Amount Waived (S$)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder={String(recordEntry.estimated_amount)}
                value={recordAmount}
                onChange={(e) => setRecordAmount(e.target.value)}
                className="input-admin w-full"
              />
              <p className="text-xs text-gray-500 mt-1">
                Defaults to the estimated {formatSGD(recordEntry.estimated_amount)} if left blank.
              </p>
            </div>

            {recordEntry.waiver_type === 'overseas_leave' && (
              <>
                <div className="form-group">
                  <label className="form-label">
                    Confirmed Return Date (re-entry to Singapore)
                  </label>
                  <input
                    type="date"
                    value={overseasReturnDate}
                    onChange={(e) => setOverseasReturnDate(e.target.value)}
                    className="input-admin w-full"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {recordEntry.return_date_pending
                      ? 'One-way ticket (Urgent Leave): set the actual re-entry date verified on return. This re-values the waivable days and amount.'
                      : 'Pre-filled from the flight ticket. Adjust if the worker’s actual re-entry differed.'}
                  </p>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    Overseas Destination <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., Bangladesh"
                    value={overseasDestination}
                    onChange={(e) => setOverseasDestination(e.target.value)}
                    className="input-admin w-full"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    For your internal record only — MOM does not require a destination for the
                    overseas-leave waiver.
                  </p>
                </div>
              </>
            )}

            {recordEntry.waiver_type === 'hospitalisation' && (
              <>
                <div className="form-group">
                  <label className="form-label">Medical Institution</label>
                  <input
                    type="text"
                    placeholder="e.g., Singapore General Hospital"
                    value={medicalInstitution}
                    onChange={(e) => setMedicalInstitution(e.target.value)}
                    className="input-admin w-full"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="form-group">
                    <label className="form-label">Doctor's Name</label>
                    <input
                      type="text"
                      value={doctorName}
                      onChange={(e) => setDoctorName(e.target.value)}
                      className="input-admin w-full"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">MC Number</label>
                    <input
                      type="text"
                      value={mcNumber}
                      onChange={(e) => setMcNumber(e.target.value)}
                      className="input-admin w-full"
                    />
                  </div>
                </div>
              </>
            )}
          </form>
        )}
      </Modal>

      {/* Submission-detail modal (WP Online packet) */}
      <LevyWaiverSubmissionModal
        leaveApplicationId={detailId}
        onClose={() => setDetailId(null)}
      />
    </div>
  );
};

export default LevyWaiverTracker;
